import { useState } from "react"
import { Check, RotateCcw, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import type { CommentView, Thread } from "@/lib/prompt-audit-api"

const ANCHOR_NOTE: Record<string, string> = {
  moved: "Text moved since this comment",
  outdated: "This text is no longer in the prompt",
}

function when(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

function author(email: string): string {
  return email.split("@")[0]
}

function Comment({ comment, canWrite, onDelete }: { comment: CommentView; canWrite: boolean; onDelete: (id: string) => void }) {
  return (
    <div className="group/comment">
      <div className="flex items-baseline gap-2">
        <span className="text-[13px] font-medium text-foreground">{author(comment.author_email)}</span>
        <time dateTime={comment.created_at} className="text-xs text-muted-foreground">{when(comment.created_at)}</time>
        {comment.mine && canWrite && (
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto opacity-0 group-hover/comment:opacity-100 focus-visible:opacity-100"
            aria-label="Delete comment"
            onClick={(event) => {
              event.stopPropagation()
              onDelete(comment.id)
            }}
          >
            <Trash2 />
          </Button>
        )}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{comment.body}</p>
    </div>
  )
}

type ThreadCardProps = {
  thread: Thread
  active: boolean
  canWrite: boolean
  busy: boolean
  onActivate: () => void
  onReply: (body: string) => Promise<boolean>
  onResolve: (resolved: boolean) => void
  onDelete: (commentId: string) => void
}

export function ThreadCard({ thread, active, canWrite, busy, onActivate, onReply, onResolve, onDelete }: ThreadCardProps) {
  const [draft, setDraft] = useState("")
  const resolved = Boolean(thread.resolved_at)
  const note = ANCHOR_NOTE[thread.anchor.status]
  return (
    <article
      aria-label={`Comment by ${author(thread.author_email)}`}
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && event.key === "Enter") onActivate()
      }}
      className={cn(
        "rounded-xl border bg-card p-3 text-left transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "shadow-md ring-1 ring-amber-400/60" : "shadow-xs hover:shadow-sm",
        resolved && "opacity-70",
      )}
    >
      {note && (
        <p className="mb-2 text-xs font-medium text-amber-700 dark:text-amber-300">{note}</p>
      )}
      {(thread.anchor.status !== "current" || active) && (
        <blockquote className="mb-2 line-clamp-3 border-l-2 border-amber-400/70 pl-2 text-xs text-muted-foreground">
          {thread.quote}
        </blockquote>
      )}
      <Comment comment={thread} canWrite={canWrite} onDelete={onDelete} />
      {thread.replies.length > 0 && (
        <div className="mt-3 space-y-3 border-t pt-3">
          {thread.replies.map((reply) => <Comment key={reply.id} comment={reply} canWrite={canWrite} onDelete={onDelete} />)}
        </div>
      )}
      {resolved && (
        <p className="mt-2 text-xs text-muted-foreground">Resolved by {author(thread.resolved_by_email ?? "")}</p>
      )}
      {active && canWrite && (
        <div className="mt-3 space-y-2" onClick={(event) => event.stopPropagation()}>
          {!resolved && (
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Reply"
              aria-label="Reply"
              className="min-h-10 text-sm"
              maxLength={4000}
            />
          )}
          <div className="flex items-center gap-2">
            {!resolved && (
              <Button
                size="sm"
                disabled={busy || !draft.trim()}
                onClick={async () => {
                  if (await onReply(draft)) setDraft("")
                }}
              >
                Reply
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onResolve(!resolved)} className="ml-auto">
              {resolved ? <RotateCcw /> : <Check />}
              {resolved ? "Reopen" : "Resolve"}
            </Button>
          </div>
        </div>
      )}
    </article>
  )
}

type ComposerProps = {
  quote: string
  busy: boolean
  onSubmit: (body: string) => Promise<boolean>
  onCancel: () => void
}

export function CommentComposer({ quote, busy, onSubmit, onCancel }: ComposerProps) {
  const [draft, setDraft] = useState("")
  return (
    <form
      className="rounded-xl border bg-card p-3 shadow-md ring-1 ring-amber-400/60"
      onSubmit={async (event) => {
        event.preventDefault()
        if (draft.trim() && (await onSubmit(draft))) setDraft("")
      }}
    >
      <blockquote className="mb-2 line-clamp-3 border-l-2 border-amber-400/70 pl-2 text-xs text-muted-foreground">{quote}</blockquote>
      <Textarea
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onCancel()
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) event.currentTarget.form?.requestSubmit()
        }}
        placeholder="Add a comment"
        aria-label="Comment"
        className="min-h-16 text-sm"
        maxLength={4000}
      />
      <div className="mt-2 flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={busy || !draft.trim()}>Comment</Button>
      </div>
    </form>
  )
}
